import { ReactNode } from "react";
import { Navigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Mail, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const { toast } = useToast();
  const [isResending, setIsResending] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if email is confirmed
  if (!user.email_confirmed_at) {
    const handleResend = async () => {
      setIsResending(true);
      try {
        const { error } = await supabase.auth.resend({ type: 'signup', email: user.email! });
        if (error) throw error;
        toast({
          title: "Email reenviado! 📧",
          description: "Verifica tua caixa de entrada (e spam!).",
        });
      } catch {
        toast({
          title: "Erro ao reenviar",
          description: "Tente novamente em alguns segundos.",
          variant: "destructive",
        });
      } finally {
        setIsResending(false);
      }
    };

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-8">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Confirma teu email</h2>
          <p className="text-muted-foreground mb-6">
            Enviamos um link de confirmação para{" "}
            <span className="font-medium text-foreground">{user.email}</span>.
            <br />Clica no link pra ativar tua conta.
          </p>
          <div className="space-y-3">
            <Button onClick={handleResend} variant="outline" className="w-full" disabled={isResending}>
              {isResending ? "Reenviando..." : (<><RefreshCw className="w-4 h-4 mr-2" />Reenviar email</>)}
            </Button>
            <Link to="/login" className="block text-sm text-muted-foreground hover:text-foreground">
              ← Voltar para o login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
